import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { GripVertical } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import type { CSSProperties } from 'react';
import type { FileItem } from '@/types/api';
import { Badge } from '@/components/ui/Badge';
import { FileActions } from './FileActions';
import { FileTypeIcon } from './FileTypeIcon';
import { useIsClient } from '@/hooks/useIsClient';

import { formatBytes } from '@/lib/formatBytes';

interface Props {
  file: FileItem;
  workspaceId?: string;
  /** When provided, renders a checkbox for bulk selection. */
  selected?: boolean;
  onSelectChange?: (next: boolean) => void;
  /** Highlights this row as the keyboard-focused item. */
  isActive?: boolean;
  canManageFiles?: boolean;
  onContextMenu?: (e: React.MouseEvent, file: FileItem) => void;
}

export interface FileDragData {
  type: 'file';
  fileId: string;
  fileName: string;
  currentFolderId: string;
}

export function FileRow({ file, workspaceId, selected, onSelectChange, isActive, canManageFiles, onContextMenu }: Props) {
  const isClient = useIsClient();

  // Draggable wraps just the row; we attach `listeners` to the GripVertical
  // handle so click-to-open / checkbox / actions inside the row keep working.
  const dragData: FileDragData = {
    type: 'file',
    fileId: file.id,
    fileName: file.name,
    currentFolderId: file.folder_id,
  };
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `file:${file.id}`,
    data: dragData,
    disabled: file.is_deleted,
  });

  const transformStyle: CSSProperties | undefined = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const selectable = onSelectChange !== undefined;
  const fullModified = format(new Date(file.updated_at), 'PPpp');

  return (
    <tr
      ref={setNodeRef}
      style={transformStyle}
      className={`border-b border-slate-100 hover:bg-slate-50 group${file.is_deleted ? ' opacity-60' : ''}${selected ? ' bg-bb-navy/5' : ''}${isDragging ? ' opacity-50' : ''}${isActive ? ' ring-1 ring-bb-navy/40 ring-inset' : ''}`}
      onContextMenu={onContextMenu ? (e) => onContextMenu(e, file) : undefined}
    >
      {selectable && (
        <td className="py-2 pl-3 pr-1 w-8">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={(e) => onSelectChange?.(e.target.checked)}
            aria-label={`Select ${file.name}`}
            className="accent-bb-navy"
          />
        </td>
      )}
      <td className="py-2 px-2 sm:px-3 min-w-0 max-w-[50vw] sm:max-w-none">
        <div className="flex items-center gap-1 min-w-0">
          {/* Drag handle: only visible on hover, only this element initiates a
              drag — keeps the link clickable and the checkbox usable.
              Hidden for client users. */}
          {canManageFiles && !isClient && (
            <button
              type="button"
              className={`shrink-0 p-0.5 text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 focus:opacity-100${file.is_deleted ? ' invisible' : ''}`}
              aria-label={`Drag ${file.name}`}
              title="Drag to move"
              {...listeners}
              {...attributes}
            >
              <GripVertical size={14} />
            </button>
          )}
          <Link
            to={`/files/${file.id}`}
            className="inline-flex items-center gap-2 text-slate-800 hover:text-bb-navy max-w-full min-w-0"
            title={file.name}
          >
            <FileTypeIcon name={file.name} size={16} />
            <span className={`truncate${file.is_deleted ? ' line-through' : ''}`}>{file.name}</span>
          </Link>
          {file.is_deleted && (
            <Badge tone="red" className="ml-2">Deleted</Badge>
          )}
        </div>
      </td>
      {canManageFiles && (
        <td className="py-2 px-3 text-sm text-slate-600 whitespace-nowrap hidden sm:table-cell">
          {formatBytes(file.size_bytes)}
        </td>
      )}
      <td className="py-2 px-3 text-sm text-slate-600 whitespace-nowrap hidden sm:table-cell" title={fullModified}>
        {format(new Date(file.updated_at), 'M/d/yyyy')}
      </td>
      {canManageFiles && (
        <td
          className="py-2 px-3 text-sm text-slate-600 whitespace-nowrap max-w-[180px] truncate hidden lg:table-cell"
          title={file.uploader_name ?? undefined}
        >
          {file.uploader_name ?? '—'}
        </td>
      )}
      <td className="py-2 px-3 text-right sticky right-0 bg-white group-hover:bg-slate-50 dark:bg-[#0E0E1F]">
        <FileActions file={file} workspaceId={workspaceId} canManage={canManageFiles} />
      </td>
    </tr>
  );
}
